/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ReactNode } from "react";
import { createContext, useEffect, useState } from "react";
import { VisuallyHidden } from "react-aria";

import { useDynamicViewport } from "../../../../hooks/useDynamicViewport";
import { VIEWPORT_TABLET } from "../../../../lib/constants/viewports/viewportTablet";
import type { DropdownMenuProps } from "../DropdownMenu/DropdownMenu.emotion";
import { DropdownMenu } from "../DropdownMenu/DropdownMenu.emotion";
import type { ModalProps } from "../Modal/Modal.emotion";
import { Modal } from "../Modal/Modal.emotion";
import { ModalHeader } from "../ModalHeader/ModalHeader.emotion";

export type ModalDropdownMenuContextShape = {
  close: () => void;
};

export const ModalDropdownMenuContext =
  createContext<ModalDropdownMenuContextShape>({ close: () => {} });

export type ModalDropdownMenuProps = {
  children?: ReactNode;
  closeOnScroll?: boolean;
  contentCSS?: Interpolation;
  dropdownMenuProps?: DropdownMenuProps;
  modalProps?: ModalProps;
  trigger?: ReactNode;
  minDesktopWidth?: number;
  open?: boolean;
  onOpenChange?: (newOpen: boolean) => void;
};

export const ModalDropdownMenu = ({
  children,
  closeOnScroll = false,
  contentCSS = {},
  dropdownMenuProps = {},
  modalProps = {},
  trigger,
  minDesktopWidth = VIEWPORT_TABLET,
  open,
  onOpenChange,
}: ModalDropdownMenuProps) => {
  const isDesktopViewport = useDynamicViewport({
    minimumWidth: minDesktopWidth,
    defaultState: null,
  });
  const [localOpen, setLocalOpen] = useState(false);

  useEffect(() => {
    if (localOpen !== open) {
      onOpenChange?.(localOpen);
    }
  }, [localOpen]);

  useEffect(() => {
    if (open !== localOpen) {
      setLocalOpen(open);
    }
  }, [open]);

  if (isDesktopViewport === null) return null;

  if (isDesktopViewport) {
    return (
      <ModalDropdownMenuContext.Provider
        value={{ close: () => setLocalOpen(false) }}
      >
        <DropdownMenu
          closeOnScroll={closeOnScroll}
          contentCSS={contentCSS}
          open={localOpen}
          onOpenChange={setLocalOpen}
          trigger={trigger}
          {...dropdownMenuProps}
        >
          {dropdownMenuProps.children || children}
        </DropdownMenu>
      </ModalDropdownMenuContext.Provider>
    );
  }

  return (
    <ModalDropdownMenuContext.Provider
      value={{ close: () => setLocalOpen(false) }}
    >
      <Modal
        contentCSS={contentCSS}
        open={localOpen}
        onOpenChange={setLocalOpen}
        trigger={trigger}
        overlayProps={{ onClick: () => setLocalOpen(false) }}
        {...modalProps}
      >
        {/* This exists to prevent Radix from throwing errors and breaking Storybook */}
        <VisuallyHidden>
          <ModalHeader />
        </VisuallyHidden>
        {modalProps.children || children}
      </Modal>
    </ModalDropdownMenuContext.Provider>
  );
};
